import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { Save, FileText, Eye } from 'lucide-react';

const TermsSettings = () => {
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await api.get('/settings/terms-and-conditions');
      setTerms(response.data.terms || '');
    } catch (error) {
      console.error('Failed to fetch terms:', error);
      toast.error('Failed to load terms');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/terms-and-conditions', { terms });
      toast.success('Terms & Conditions saved successfully!');
    } catch (error) {
      console.error('Failed to save terms:', error);
      toast.error('Failed to save terms');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Hardware Purchase Terms & Conditions</h1>
        <p className="text-slate-600 mt-2">
          Manage the terms and conditions that customers must accept for hardware-only purchases
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Terms & Conditions Content
          </CardTitle>
          <CardDescription>
            These terms will be shown to customers when they purchase hardware without services
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Enter hardware purchase terms and conditions (HTML supported)"
              className="min-h-[400px] font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-2">
              You can use HTML tags for formatting: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, etc.
            </p>
          </div>

          <div className="flex items-center justify-between pt-12">
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          {showPreview && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Preview</h3>
              <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: terms }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">💡 When are these terms shown?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• When a quote contains <strong>only hardware items</strong> (no services)</li>
          <li>• Customer must check a box to accept before proceeding</li>
          <li>• Acceptance is recorded with timestamp for legal compliance</li>
        </ul>
      </div>
    </div>
  );
};

export default TermsSettings;
